import { Injectable } from "@angular/core";

import { Observable } from "rxjs";

import { AiRole, AIRoleInstruction, CreateRoleRequest, UpdateRoleRequest } from "@shared/models/ai-role.model";
import { BaseHttpService } from "@shared/services/base-http.service";

@Injectable({
  providedIn: "root"
})
export class AIRolesHttpService extends BaseHttpService {
  protected override baseEndpoint = "ai-roles";

  getRoles(searchQuery?: string, sortOrder: "asc" | "desc" = "desc"): Observable<AiRole[]> {
    const params: Record<string, string> = {};
    if (searchQuery) {
      params.search = searchQuery;
    }
    if (sortOrder) {
      // Use sort_by format: -createdAt for desc, +createdAt for asc
      params.sort_by = sortOrder === "desc" ? "-createdAt" : "+createdAt";
    }
    return this.getManyWithoutPagination({
      type: AiRole,
      options: Object.keys(params).length > 0 ? { params } : undefined
    });
  }

  getRole(roleId: string): Observable<AiRole> {
    return this.get<AiRole>({
      endpoint: roleId,
      type: AiRole
    });
  }

  createRole(role: CreateRoleRequest): Observable<AiRole> {
    return this.post<AiRole>({
      body: role,
      type: AiRole
    });
  }

  updateRole(roleId: string, role: UpdateRoleRequest): Observable<AiRole> {
    return this.patch<AiRole>({
      endpoint: roleId,
      body: role,
      type: AiRole
    });
  }

  deleteRole(roleId: string): Observable<void> {
    return this.delete<void>({
      endpoint: roleId
    });
  }

  addInstruction(roleId: string, instruction: { text: string }): Observable<AIRoleInstruction> {
    return this.post<AIRoleInstruction>({
      endpoint: `${roleId}/instructions`,
      body: instruction,
      type: AIRoleInstruction
    });
  }

  updateInstruction(instructionId: string, instruction: Partial<AIRoleInstruction>): Observable<AIRoleInstruction> {
    return this.patch<AIRoleInstruction>({
      endpoint: `instructions/${instructionId}`,
      body: instruction,
      type: AIRoleInstruction
    });
  }

  deleteInstruction(instructionId: string): Observable<void> {
    return this.delete<void>({
      endpoint: `instructions/${instructionId}`
    });
  }

  // --- Sharing ---

  shareWithUser(roleId: string, recipient: string): Observable<void> {
    return this.post<void>({
      endpoint: `${roleId}/share/user`,
      body: { recipient }
    });
  }

  shareWithOrganization(roleId: string): Observable<void> {
    return this.post<void>({
      endpoint: `${roleId}/share/organization`
    });
  }

  stopSharingWithOrganization(roleId: string): Observable<void> {
    return this.delete<void>({
      endpoint: `${roleId}/share/organization`
    });
  }

  checkOrgShare(roleId: string): Observable<{ isShared: boolean }> {
    return this.get<{ isShared: boolean }>({
      endpoint: `${roleId}/share/organization`
    });
  }

  importRole(roleId: string): Observable<AiRole> {
    return this.post<AiRole>({
      endpoint: `${roleId}/import`,
      type: AiRole
    });
  }
}
